import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type InvView = 'users' | 'drones';

export type UserFilterKey = 'ACTIVE' | 'INACTIVE' | 'ADMIN';

// Mockup drone filter stack — 5 axes, AND across axes, OR within an axis.
// Keys are `${axis}:${value}`; every chip defaults ON so all drones show.
export type DroneFilterKey =
  | 'adapter:MAVLink'
  | 'adapter:DJI'
  | 'adapter:ADS-B Out'
  | 'adapter:Other'
  | 'protocol:MAVLink v2'
  | 'custodian:aircraft'
  | 'custodian:gcs'
  | 'custodian:none'
  | 'state:online'
  | 'state:offline'
  | 'dtwin:updated'
  | 'dtwin:outdated';

const ALL_DRONE_FILTERS: DroneFilterKey[] = [
  'adapter:MAVLink',
  'adapter:DJI',
  'adapter:ADS-B Out',
  'adapter:Other',
  'protocol:MAVLink v2',
  'custodian:aircraft',
  'custodian:gcs',
  'custodian:none',
  'state:online',
  'state:offline',
  'dtwin:updated',
  'dtwin:outdated',
];

interface InvStore {
  view: InvView;
  selectedUserId: string | null;
  selectedDroneId: string | null;
  userFilters: Record<UserFilterKey, boolean>;
  droneFilters: Record<DroneFilterKey, boolean>;
  droneSearch: string;
  setView: (v: InvView) => void;
  selectUser: (id: string | null) => void;
  selectDrone: (id: string | null) => void;
  toggleUserFilter: (k: UserFilterKey) => void;
  toggleDroneFilter: (k: DroneFilterKey) => void;
  setDroneSearch: (term: string) => void;
}

export const useInvStore = create<InvStore>()(
  devtools(
    (set) => ({
      // Mockup defaults to Drones view with the first drone pre-selected.
      view: 'drones',
      selectedUserId: null,
      selectedDroneId: null,
      userFilters: { ACTIVE: true, INACTIVE: false, ADMIN: false },
      droneFilters: Object.fromEntries(ALL_DRONE_FILTERS.map((k) => [k, true])) as Record<
        DroneFilterKey,
        boolean
      >,
      droneSearch: '',
      // Mockup clears the detail panel to its placeholder on a view switch.
      setView: (v) => set({ view: v, selectedUserId: null, selectedDroneId: null }),
      selectUser: (id) => set({ selectedUserId: id, selectedDroneId: null }),
      selectDrone: (id) => set({ selectedDroneId: id, selectedUserId: null }),
      toggleUserFilter: (k) =>
        set((s) => ({ userFilters: { ...s.userFilters, [k]: !s.userFilters[k] } })),
      toggleDroneFilter: (k) =>
        set((s) => ({ droneFilters: { ...s.droneFilters, [k]: !s.droneFilters[k] } })),
      setDroneSearch: (term) => set({ droneSearch: term }),
    }),
    { name: 'inv' },
  ),
);
